<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Edit2, Plus, SearchIcon, Trash2, XIcon } from '@lucide/vue';
import Alert from '@/components/v2/Alert.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Combobox, { ComboboxOption } from '@/components/v2/Combobox.vue';
import TableSkeleton from '@/components/v2/TableSkeleton.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import SubitemTypeEditModal from '@/components/SubitemTypeEditModal.vue';
import { useToast } from '@/composables/useToast';
import { subitemTypeService } from '@/services/SubitemTypeService';
import { CoverageType } from '@/types/PolicyType';
import { SubitemKind, subitemKindLabels } from '@/types/PolicySublimit';
import { SubitemType, SubitemTypeSaveRequest } from '@/types/SubitemType';

const props = defineProps<{
  kind: SubitemKind;
  coverageTypes: CoverageType[];
}>();

const toast = useToast();
const label = computed(() => ({
  singular: `${subitemKindLabels[props.kind]} Type`,
  plural: `${subitemKindLabels[props.kind]} Types`,
}));

const loading = ref(true);
const loadError = ref<string>();
const subitemTypes = ref<SubitemType[]>([]);
const coverageTypeFilter = ref('');
const searchText = ref('');
const editing = ref<SubitemType | null>(null);
const creating = ref(false);
const deleting = ref<SubitemType | null>(null);
const deletePending = ref(false);

type SortKey = 'coverageTypeName' | 'sourceKey' | 'sourceName' | 'derivedName' | 'origin';

const columns: Array<{ key: SortKey; label: string; centered?: boolean }> = [
  { key: 'coverageTypeName', label: 'Coverage Type' },
  { key: 'sourceKey', label: 'Source Key' },
  { key: 'sourceName', label: 'Source Name' },
  { key: 'derivedName', label: 'Display Name' },
  { key: 'origin', label: 'Origin', centered: true },
];
const skeletonColumns = [...columns.map((column) => column.label), ''];

const sortKey = ref<SortKey>('coverageTypeName');
const sortAscending = ref(true);

const isManual = (subitemType: SubitemType) => subitemType.sourceKey === null;
const isRenamed = (subitemType: SubitemType) => !isManual(subitemType) && subitemType.name !== null;
const originLabel = (subitemType: SubitemType) => (isManual(subitemType) ? 'Manual' : 'Epic');

const coverageTypeOptions = computed<ComboboxOption[]>(() =>
  props.coverageTypes.map((coverageType) => ({
    label: coverageType.coverageType,
    value: String(coverageType.coverageTypeId),
  })),
);

const isFiltered = computed(() => !!coverageTypeFilter.value || !!searchText.value.trim());

const sortValue = (subitemType: SubitemType, key: SortKey): string =>
  key === 'origin' ? originLabel(subitemType) : (subitemType[key] ?? '');

// Sorting happens here rather than through v-tablesort: that directive reorders <tr> nodes behind
// Vue's back, so a later filter or reload would render rows against stale positions.
const filteredSubitemTypes = computed(() => {
  const search = searchText.value.trim().toLowerCase();
  const filtered = subitemTypes.value.filter((subitemType) => {
    if (coverageTypeFilter.value && String(subitemType.coverageTypeId) !== coverageTypeFilter.value) {
      return false;
    }
    return (
      !search ||
      [subitemType.derivedName, subitemType.sourceKey, subitemType.sourceName].some((value) =>
        value?.toLowerCase().includes(search),
      )
    );
  });
  return [...filtered].sort((a, b) => {
    const result = sortValue(a, sortKey.value).localeCompare(sortValue(b, sortKey.value));
    return sortAscending.value ? result : -result;
  });
});

const epicCount = computed(() => filteredSubitemTypes.value.filter((subitemType) => !isManual(subitemType)).length);
const manualCount = computed(() => filteredSubitemTypes.value.length - epicCount.value);

function toggleSort(key: SortKey) {
  if (sortKey.value === key) {
    sortAscending.value = !sortAscending.value;
    return;
  }
  sortKey.value = key;
  sortAscending.value = true;
}

// sorted/ascending/descending are what global.css keys the ▲▼ indicators off.
function headerClass(key: SortKey) {
  return sortKey.value === key ? ['sorted', sortAscending.value ? 'ascending' : 'descending'] : [];
}

function ariaSort(key: SortKey) {
  if (sortKey.value !== key) {
    return 'none';
  }
  return sortAscending.value ? 'ascending' : 'descending';
}

function deleteBlockedReason(subitemType: SubitemType): string | undefined {
  if (!isManual(subitemType)) {
    return "Epic-sourced types can't be deleted";
  }
  if (subitemType.usageCount > 0) {
    return `In use by ${subitemType.usageCount} policy ${subitemType.usageCount === 1 ? 'record' : 'records'}`;
  }
  return undefined;
}

function handleSearchKeyUp(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    searchText.value = '';
    event.stopPropagation();
  }
}

async function load() {
  try {
    subitemTypes.value = await subitemTypeService.list(props.kind);
    loadError.value = undefined;
  } catch {
    loadError.value = `Failed to load ${label.value.plural.toLowerCase()}.`;
  } finally {
    loading.value = false;
  }
}

async function save(request: SubitemTypeSaveRequest) {
  if (editing.value) {
    await subitemTypeService.update(props.kind, editing.value.id, request);
    toast.success(`${label.value.singular} updated.`);
  } else {
    await subitemTypeService.create(props.kind, request);
    toast.success(`${label.value.singular} created.`);
  }
  await load();
}

async function confirmDelete(): Promise<boolean> {
  if (!deleting.value) {
    return true;
  }
  if (deletePending.value) {
    return false;
  }
  deletePending.value = true;
  try {
    await subitemTypeService.remove(props.kind, deleting.value.id);
    toast.success(`${label.value.singular} deleted.`);
    await load();
  } catch (error) {
    toast.error(error instanceof Error ? error.message : `Failed to delete ${label.value.singular.toLowerCase()}.`);
  } finally {
    deletePending.value = false;
  }
  return true;
}

function closeEditor() {
  editing.value = null;
  creating.value = false;
}

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-3">
      <div class="w-56 max-w-full">
        <Combobox
          v-model="coverageTypeFilter"
          :options="coverageTypeOptions"
          placeholder="All coverage types"
          input-class="input-sm"
          data-testid="subitem-type-coverage-filter" />
      </div>
      <label class="input input-sm flex w-80 max-w-full items-center gap-2">
        <SearchIcon
          :size="16"
          class="text-gray-400" />
        <input
          v-model="searchText"
          type="text"
          class="grow"
          placeholder="Search by name or source key"
          autocomplete="off"
          data-testid="subitem-type-search"
          @keyup="handleSearchKeyUp" />
        <XIcon
          v-if="searchText"
          :size="16"
          class="cursor-pointer"
          @click="searchText = ''" />
      </label>
      <AppButton
        primary
        class="btn-sm ml-auto"
        data-testid="subitem-type-new"
        @click="creating = true">
        <Plus :size="16" />
        New {{ label.singular }}
      </AppButton>
    </div>

    <div
      v-if="!loading && !loadError"
      class="flex flex-wrap gap-2">
      <span
        class=""
        data-testid="subitem-type-count-total">
        {{
          isFiltered
            ? `${filteredSubitemTypes.length} of ${subitemTypes.length} types`
            : `${subitemTypes.length} total types`
        }}
      </span>
      <span
        class="badge badge-info badge-soft badge-outline"
        data-testid="subitem-type-count-epic">
        {{ epicCount }} Epic
      </span>
      <span
        class="badge badge-success badge-soft badge-outline"
        data-testid="subitem-type-count-manual">
        {{ manualCount }} Manual
      </span>
    </div>

    <Alert
      v-if="!loading && loadError"
      type="error">
      {{ loadError }}
    </Alert>

    <TableSkeleton
      v-if="loading"
      :columns="skeletonColumns"
      :show-filter-bar="false"
      :show-title="false" />

    <div
      v-else-if="!loadError"
      class="overflow-x-auto">
      <table
        class="apptable sortable dense"
        data-testid="subitem-type-table">
        <thead>
          <tr>
            <td
              v-for="column in columns"
              :key="column.key"
              :class="[column.centered ? 'text-center' : 'text-left', headerClass(column.key)]"
              :aria-sort="ariaSort(column.key)"
              :data-testid="`subitem-type-sort-${column.key}`"
              @click="toggleSort(column.key)">
              {{ column.label }}
            </td>
            <td class="nosort actions" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredSubitemTypes.length === 0">
            <td
              colspan="6"
              class="nosort text-center italic"
              data-testid="subitem-type-empty">
              {{
                subitemTypes.length === 0
                  ? `No ${label.plural.toLowerCase()} found.`
                  : `No ${label.plural.toLowerCase()} match your filters.`
              }}
            </td>
          </tr>
          <tr
            v-for="subitemType in filteredSubitemTypes"
            :key="subitemType.id"
            :data-id="subitemType.id"
            class="group"
            data-testid="subitem-type-row">
            <td>{{ subitemType.coverageTypeName }}</td>
            <td
              class="font-mono"
              data-testid="subitem-type-source-key">
              {{ subitemType.sourceKey ?? '—' }}
            </td>
            <td
              class="text-gray-600"
              data-testid="subitem-type-source-name">
              {{ subitemType.sourceName ?? '—' }}
            </td>
            <td>
              {{ subitemType.derivedName }}
              <span
                v-if="isRenamed(subitemType)"
                class="badge badge-ghost badge-xs ml-1"
                :title="`Renamed from ${subitemType.sourceName}`"
                data-testid="subitem-type-renamed">
                renamed
              </span>
            </td>
            <td class="text-center">
              <span
                class="badge badge-soft badge-sm badge-outline"
                :class="isManual(subitemType) ? 'badge-success' : 'badge-info'">
                {{ originLabel(subitemType) }}
              </span>
            </td>
            <td class="actions">
              <div class="flex justify-end gap-1">
                <button
                  type="button"
                  class="btn btn-ghost btn-xs"
                  :title="`Edit ${subitemType.derivedName}`"
                  :data-testid="`subitem-type-edit-${subitemType.id}`"
                  @click="editing = subitemType">
                  <Edit2 :size="16" />
                </button>
                <span :title="deleteBlockedReason(subitemType) ?? `Delete ${subitemType.derivedName}`">
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs enabled:hover:text-red-400"
                    :disabled="!!deleteBlockedReason(subitemType)"
                    :data-testid="`subitem-type-delete-${subitemType.id}`"
                    @click="deleting = subitemType">
                    <Trash2 :size="16" />
                  </button>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <SubitemTypeEditModal
      v-if="editing || creating"
      :kind="kind"
      :subitem-type="editing"
      :coverage-types="coverageTypes"
      :save="save"
      @closed="closeEditor" />

    <ConfirmModal
      v-if="deleting"
      :title="`Delete ${label.singular.toLowerCase()}`"
      :subtitle="deleting.derivedName"
      :action="confirmDelete"
      :is-confirm-disabled="() => deletePending"
      confirm-text="Delete"
      size="sm"
      :show="true"
      @closed="deleting = null">
      <p class="p-2">This {{ label.singular.toLowerCase() }} will be permanently removed. Are you sure?</p>
    </ConfirmModal>
  </div>
</template>
